/* eslint-disable max-lines */
// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapActions, mapState } from "vuex";
import api from "../../../common/api";
import alert from "../../../share/alert";
import cstmTable from "../../../share/components/vue/PaginationTable";
import loading from "../../../share/components/vue/ProcessingIcon";
import config from "../../../share/config";
import individualDiagnose from "../../../share/util/individualDiagnose";
import util from "../../../share/util/utils";
import logWrapper from "../../../share/logWrapper";
import dataMonitorExecute from "../../vue/dataMonitor/DataMonitorMeasure";
import dmUtil from "../../pages/dataMonitor/dataMonitorUtil";

const SIGNAL_GROUP_I18N_PREFIX = 'individual.select_signal_group_';
const COLUMN = {
  NAME: 'menu_title',
  SYSTEM: 'system_name'
};
const reader = new FileReader();
const moment = require('moment');

/** モーダル表示用フェーズ定義 */
const PHASE = {
  CONFIRM_IMPORT: 'confirmImport',
  CONFIRM_EXPORT: 'confirmExport',
  IMPORT_SUCCESS: 'success',
  ERROR_FORMAT: 'errorFormat',
  ERROR_SYSTEM_ID: 'errorSystemId'
};

/**エラーメッセージ定義 */
const MESSAGE = {
  ERROR_SYSTEM_ID: 'invalid_system_id'
};

export default {
  name: "dataMonitorSelect",
  data() {
    return {
      processing: false,
      cstmTableColumns: [],
      systemTableColumns: [],
      // 画面に表示する為の機能リスト
      filterFunctionList: [],
      filterRemoveFunctionList: [],
      // 選択中の機能オブジェクト
      selectedFunction: {},
      selectedFunctions: [],
      selectedRemoveFunction: {},
      selectedRemoveFunctions: [],
      selectedFunctionList: [],
      selectedFunctionListPre: [],
      showDataMonitorExecute: false,
      dataMonitorList: [],
      // フィルター用テキスト
      filterText: "",
      filterTextRemove: "",
      // データモニタ用のi18nKey
      i18nDmKey: "",
      menuResponse: null,
      params: null,
      systemId: [],
      previousName: "",
      queryName: "",
      sortAsc: true, // 項目並び順
      //リクエストID
      response: "",
      //モニタ項目
      monitorResponse: null,
      //項目ソート
      currentSortkey: "",
      // 選択テーブルのソート状態
      selectedTableSort: {
        currentSortKey: '',
        column: {
          [COLUMN.NAME]: config.ASC,
          [COLUMN.SYSTEM]: config.ASC,
        }
      },
      // デバッグ環境か否か
      isDebug: process.env.VUE_APP_DEBUG_LOG && process.env.VUE_APP_DEBUG_LOG === "true",
      // デバッグ用のコンポーネントを表示するか否か
      showDebug: false,
      selectedSignalGroup: null,
      // APIから返された全信号線グループ
      signalGroupList: [],
      // 選択中のシステムでフィルタした信号線グループ
      filteredSignalGroupList: [],
      signalGroupItems: new Map(),
      // HTMLにてログ出力するため定義
      logWrapper: logWrapper,
      // プルダウンで選択されたシステム
      selectedSystem: null,
      // 項目リスト対応
      importFile: null,
      fileName: "",
      showCstmListModal: false,
      phase: null
    };
  },

  props: {
    // システム間移動判定
    isUseCache: undefined,
    // DM/AT判定
    isDataMonitor: { type: Boolean, default: false, required: true },
    // 特殊機能判定
    isSpecial: { type: Boolean, default: false, required: false },
    // DM機能の設定インスタンス
    dataMonitorFunctionSettings: { type: Object, required: true },
    // HEV系ATのDM１行目にIO/Routine Control結果を表示させる対応
    selectedAtFunc: { type: Object, default: {}, required: false },
    isHev: { type: Boolean, default: false, required: false },
    atInfo: { type: Array, default: [], required: false },
  },

  components: {
    "processing-view": loading,
    "cstm-table": cstmTable,
    "data-monitor-execute": dataMonitorExecute,
  },

  watch: {
    language: {
      immediate: true,
      handler(val) {
        val; // To avoid warning
        if (!this.monitorResponse) {
          return;
        }
        const menuList = this.getDmi18nMonitorItemList(
          this.monitorResponse,
        );
        this.filterFunctionList = this.escapeHevTarget(menuList);
        this.originalFunctionList = menuList;
        this.selectedFunctionList = this.setSelectedFunctionList(
          this.getDmi18nMenuSelectedList(this.selectedFunctionList)
        );
        this.selectedFunctionListPre = this.selectedFunctionList;
        this.updateFilter();
        this.filterRemoveFunctionList = this.selectedFunctionList;
        this.updateFilterRemove();
        this.setColumns();
        this.signalGroupList = this.signalGroupList.map((signal) => {
          return { value: signal.value, text: this.$t(`${SIGNAL_GROUP_I18N_PREFIX}${signal.value}`), system: signal.system };
        });
        // システムでフィルタ済み信号線グループも更新
        this.filteredSignalGroupList = this.signalGroupList.filter(signal => (
          !signal.system || signal.system === this.selectedSystem
        ));
      },
    },

    isUseCache: {
      immediate: true,
      async handler(val) {
        if ((val === null) || (val === undefined)) {
          return;
        }
        this.setColumns();
        if (this.isDataMonitor) {
          await this.updateTable();
        } else {
          await this.updateTableForAT();
        }

        // 全項目追加
        this.appendFunction(this.originalFunctionList);

        // 自動実行モードの場合、DM開始
        if (this.dataMonitorFunctionSettings.isAutoExecute) {
          // 全選択
          this.handleAllAppendButtonClick();
          // 計測開始
          this.startExecute();
        }
      },
    },

    systemId: {
      immediate: true,
      handler(val) {
        // 選択システム変更時、システムプルダウンのデフォルト値は先頭のシステム
        if (val && val.length > 0) {
          this.selectedSystem = val[0];
        }
      }
    }
  },

  computed: {
    ...mapState({
      language: (state) => state.systems.language,
      netAppId: (state) => state.netAppConnector.pairingAppId,
      userProperty: (state) => state.userProperty,
      individualMenus: state => state.vehicle.individualMenus,
    }),

    itemComparator() {
      if (this.sortAsc) {
        return (a, b) => a.sort_id - b.sort_id;
      } else {
        return (a, b) => b.sort_id - a.sort_id;
      }
    },

    // システムのドロップダウンリスト表示設定
    translatedSystemName() {
      return this.systemId.map(id => ({
        value: id,
        text: this.$t('diagSytem.' + id) + '(' + dmUtil.getAbbrSysName(id) + ')',
      }));
    },

    // 項目リスト関連のモーダル設定
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR_FORMAT:
        case PHASE.ERROR_SYSTEM_ID:
          return 'danger';
        case PHASE.IMPORT_SUCCESS:
          return 'success';
        case PHASE.CONFIRM_IMPORT:
        case PHASE.CONFIRM_EXPORT:
        default:
          return 'secondary';
      }
    },

    /**
     * 表示ヘッダー文字列判定
     * @returns {string} ヘッダー文字列
     */
    headerTitle() {
      const ConfirmTitle = 'message.common_title_confirmation';
      const SuccessTitle = 'message.common_title_success';
      const ErrorTitle = 'message.common_title_error';

      const titleMap = {
        [PHASE.CONFIRM_IMPORT]: ConfirmTitle,
        [PHASE.CONFIRM_EXPORT]: ConfirmTitle,
        [PHASE.IMPORT_SUCCESS]: SuccessTitle,
        [PHASE.ERROR_FORMAT]: ErrorTitle,
        [PHASE.ERROR_SYSTEM_ID]: ErrorTitle
      };

      return this.$t(titleMap[this.phase]);
    },

    /**
     * モーダル表示メッセージ判定
     * @returns {string} メッセージ
     */
    modalMessage() {
      const messageMap = {
        [PHASE.CONFIRM_IMPORT]: `individual.label_data_monitor_item_list_import_confirm`,
        [PHASE.CONFIRM_EXPORT]: `individual.label_data_monitor_item_list_export_confirm`,
        [PHASE.IMPORT_SUCCESS]: `individual.label_data_monitor_item_list_import_success`,
        [PHASE.ERROR_FORMAT]: `individual.label_data_monitor_item_list_import_invalid_format`,
        [PHASE.ERROR_SYSTEM_ID]: `individual.label_data_monitor_item_list_import_invalid_system_id`
      };

      return this.$t(messageMap[this.phase]);
    },

    /**
     * [OK]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonOk() {
      const showOkButtonMap = {
        [PHASE.CONFIRM_IMPORT]: true,
        [PHASE.CONFIRM_EXPORT]: true,
        [PHASE.IMPORT_SUCCESS]: true,
        [PHASE.ERROR_FORMAT]: true,
        [PHASE.ERROR_SYSTEM_ID]: true
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * OKボタンの押下可否判定
     * @returns {boolean} trueなら押下可能
     */
    isAbleOkButton() {
      // PHASE.CONFIRM_IMPORT 以外は常時押下可能
      if (this.phase !== PHASE.CONFIRM_IMPORT) return true;
      // インポート対象のファイルが指定されていれば押下可能
      return this.importFile ? true : false;
    },

    /**
     * エクスポートボタンの押下可否判定
     * @returns {boolean} trueなら押下可能
     */
    isAbleExportButton() {
      // 項目未選択時は押下不可
      if (this.selectedFunctionList.length === 0) {
        return false;
      } else {
        return true;
      }
    },

    /**
     * [キャンセル]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.CONFIRM_IMPORT]: true,
        [PHASE.CONFIRM_EXPORT]: true,
        [PHASE.IMPORT_SUCCESS]: false,
        [PHASE.ERROR_FORMAT]: false,
        [PHASE.ERROR_SYSTEM_ID]: false
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * インポートファイル選択の表示判定
     * @returns {boolean} trueなら表示
     */
    showInputArea() {
      const showInputAreaMap = {
        [PHASE.CONFIRM_IMPORT]: true,
        [PHASE.CONFIRM_EXPORT]: false,
        [PHASE.IMPORT_SUCCESS]: false,
        [PHASE.ERROR_FORMAT]: false,
        [PHASE.ERROR_SYSTEM_ID]: false
      };
      let result = showInputAreaMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /**
     * componentのmounted時に呼び出す処理
     */
    mountedLogic() {
      //      this.setColumns();
      //      this.updateTable();
    },

    /**
     * データモニタ項目一覧取得
     * @returns GetのResponse | データモニタ項目
     */
    async getMonitorItemsResponse() {
      // システムごとの項目一覧取得のPromiseを作成し、Promise.allで並列でまとめて実行
      const promises = this.systemId.map((systemName) => {
        return individualDiagnose.syncApi(
          this,
          // GET API 呼び出しのコールバック
          async () => {
            return await api.getParamCall(
              config.DMAT,
              "/netApps/" +
              this.netAppId +
              "/systems/" +
              systemName +
              "/monitorItems",
              {
                // アクティブテストの場合、常にfalseとする
                analysis: this.isSpecial && this.isDataMonitor,
                is_active_test: !this.isDataMonitor,
              }
            );
          },
          // エラー発生のコールバック
          (errorType) => {
            errorType;
            this.showUnexpectedError();
          },
          // GET API レスポンスチェックのコールバック
          (getApiResponse) => {
            // monitor_items が入っていれば成功とする
            return getApiResponse &&
              getApiResponse.data &&
              getApiResponse.data.monitor_items
              ? ""
              : config.INDIVIDUAL_API_CALL_ERROR_GET_INVALID_RESPONSE;
          },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => {
            this.validateErrorResult(result);
          }
        );
      });

      const tempAllResponses = await Promise.all(promises);
      // syncApiの処理失敗時は response が null になるので考慮
      const allResponses = tempAllResponses.filter((response => response));
      if (allResponses.length === 0) {
        return null;
      }

      const mergeResponse = {
        monitor_items: [],
        signal_group: [],
        custom_list: []
      };

      // monitor_items, signal_group, custom_listの中身を結合して、1つのオブジェクトにする
      allResponses.forEach((response, index) => {
        // 1画面で複数システムを扱えるように、各信号線項目のキーにシステム名を付与する
        if (response.data.monitor_items) {
          const addedSystemItems = response.data.monitor_items.map(item => ({ ...item, id: dmUtil.setSystem(this.systemId[index], item.id) }));
          mergeResponse.monitor_items = mergeResponse.monitor_items.concat(addedSystemItems);
        }

        /* HEV系AT対応 */
        // 表示順序最小値を求める
        let minimumDisplayOrder = 0;
        for (let i = 0; i < mergeResponse.monitor_items.length; i++) {
          minimumDisplayOrder = minimumDisplayOrder > mergeResponse.monitor_items[i].monitor_display_order ?
            mergeResponse.monitor_items[i].monitor_display_order : minimumDisplayOrder;
        }

        // 既に存在してなければ、対象追加
        if (this.isHev) {
          for (let i = 0; i < this.atInfo.length; i++) {
            const sameItemIndex = mergeResponse.monitor_items.findIndex(item =>
              this.atInfo[i].id === dmUtil.getNoSystemItemId(item.id)
            );
            if (sameItemIndex !== -1) {
              // atInfoに同じDIDが含まれる場合、monitor_display_orderを最小に変更
              mergeResponse.monitor_items[sameItemIndex].monitor_display_order = minimumDisplayOrder - 1;
            } else {
              // 存在していない場合は、対象項目を追加
              mergeResponse.monitor_items = mergeResponse.monitor_items.concat({
                id: dmUtil.setSystem(this.systemId[index], this.atInfo[i].id),
                monitor_display_order: minimumDisplayOrder - 1
              });
            }
          }
        }

        // 信号グループ
        if (response.data.signal_group) {
          const addedSystemGroups = response.data.signal_group.map(group => ({
            ...group,
            id_list: group.id_list.map(itemId => dmUtil.setSystem(this.systemId[index], itemId)),
            // 選択中のシステムでフィルタするため、どのシステムの信号線かわかるようにシステムを追加
            system: this.systemId[index]
          }));
          mergeResponse.signal_group = mergeResponse.signal_group.concat(addedSystemGroups);
        }

        // カスタムリスト
        if (response.data.custom_list) {
          const addedSystemList = response.data.custom_list.map(i => dmUtil.setSystem(this.systemId[index], i));
          mergeResponse.custom_list = mergeResponse.custom_list.concat(addedSystemList);
        }
      });

      this.updateAdditionalInfo(mergeResponse);
      return mergeResponse.monitor_items;
    },

    /**
     * カスタムリストと信号グループ情報の更新を行う
     * @param {*} response レスポンス情報
     */
    updateAdditionalInfo(response) {
      // 信号グループ情報の初期化
      this.signalGroupList = [
        { value: "all_data", text: this.$t(`${SIGNAL_GROUP_I18N_PREFIX}all_data`) },
        { value: "custom_list", text: this.$t(`${SIGNAL_GROUP_I18N_PREFIX}custom_list`) }
      ];

      // 信号グループ情報にAll Dataとカスタムリストのレスポンス値を設定
      this.signalGroupItems = new Map([
        ['all_data', response.monitor_items ? response.monitor_items.map((item) => item.id) : []],
        ['custom_list', response.custom_list ? response.custom_list : []]
      ]);

      // 信号グループ情報に追加のリストを設定
      if (response.signal_group) {
        const uniqueSignalGroupList = response.signal_group.map((signal) => {
          this.signalGroupItems.set(signal.group_name, signal.id_list);
          return { value: signal.group_name, text: this.$t(`${SIGNAL_GROUP_I18N_PREFIX}${signal.group_name}`), system: signal.system };
        });
        this.signalGroupList = this.signalGroupList.concat(uniqueSignalGroupList);
        // 選択中システムで信号線グループをフィルタ
        this.filteredSignalGroupList = this.signalGroupList.filter(signal => (
          !signal.system || signal.system === this.selectedSystem
        ));
      }
    },

    /**
     * テーブル情報の更新を行う
     */
    async updateTable() {
      try {
        this.systemId = dmUtil.getSystemIds(this);
        this.processing = true;
        // 自動実行モードの場合、Loadingアイコンを表示
        if (this.dataMonitorFunctionSettings.isAutoExecute) {
          this.$emit("display-processing", true);
        }

        // データモニター一覧取得のポーリング処理を行う
        const response = await individualDiagnose.pollingApi(
          this,
          // POST API 呼び出しのコールバック
          async () => {
            return await api.postCall(
              config.DMAT,
              "/netApps/" +
              this.netAppId +
              "/initialize",
              {
                use_cache: this.isUseCache,
                is_active_test: false,
                system_name: this.systemId
              }
            );
          },
          // GET API 呼び出しのコールバック
          async (postApiResponse) => {
            return await api.getCall(
              config.DMAT,
              "/netApps/" +
              this.netAppId +
              "/initialize/requests/" +
              postApiResponse.data.request_id
            );
          },
          // エラー発生のコールバック
          (errorType) => {
            errorType;
            this.showUnexpectedError();
          },
          // POST API レスポンスチェックのコールバック
          (postApiResponse) => {
            // リクエストID が正常に返ってきたら成功とする
            return postApiResponse &&
              postApiResponse.data &&
              postApiResponse.data.request_id
              ? ""
              : config.INDIVIDUAL_API_CALL_ERROR_POST_INVALID_RESPONSE;
          },
          // GET API レスポンスチェックのコールバック
          (getApiResponse) => {
            let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
            if (
              getApiResponse &&
              getApiResponse.data &&
              getApiResponse.data.status
            ) {
              switch (getApiResponse.data.status) {
                case config.STATUS_DMAT_COMPLETED:
                  // ポーリング終了を返す
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                case config.STATUS_DMAT_IN_PROGRESS:
                  // ポーリング続行を返す
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
                case config.STATUS_DMAT_FAILED:
                default:
                  // それ以外はエラー発生を返す
                  break;
              }
            }
            return result;
          },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => {
            this.validateErrorResult(result);
          },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => {
            this.validateErrorResult(result);
          }
        );

        if (response) {
          // ポーリングが正常に完了した場合は、一覧データ取得処理を行う
          this.monitorResponse = await this.getMonitorItemsResponse();
          // responseが存在しない場合、処理終了
          if (!this.monitorResponse) {
            return;
          }
          // データモニタ一覧の多言語変換リストの取得
          const monitorDataList = this.getDmi18nMonitorItemList(
            this.monitorResponse,
          );
          this.originalFunctionList = monitorDataList;
          // 最初に選択されたシステム名の項目を表示
          this.filterFunctionList = this.escapeHevTarget(this.filterBySystem(monitorDataList));
          this.updateFilter();

          // 表示項目がない場合、非サポート画面を表示
          if (this.originalFunctionList.length < 1) {
            // OK押下時は何もしない
            alert.showConfirmation(this, null, this.$t('individual.label_not_supported_message'), null, null, null, true);
          }
        }
      } finally {
        this.processing = false;
        // 自動実行モードの場合、Loadingアイコンを非表示
        if (this.dataMonitorFunctionSettings.isAutoExecute) {
          this.$emit("display-processing", false);
        }
      }
    },

    /**
     * アクティブテスト向けテーブル情報の更新を行う
     */
    async updateTableForAT() {
      this.systemId = dmUtil.getSystemIds(this);
      try {
        this.processing = true;

        // ポーリングが正常に完了した場合は、一覧データ取得処理を行う
        this.monitorResponse = await this.getMonitorItemsResponse();
        // responseが存在しない場合、処理終了
        if (!this.monitorResponse) {
          return;
        }
        // データモニタ一覧の多言語変換リストの取得
        const monitorDataList = this.getDmi18nMonitorItemList(
          this.monitorResponse
        );
        this.originalFunctionList = monitorDataList;
        this.filterFunctionList = this.escapeHevTarget(monitorDataList);
        // DM領域1行目にIOControl,RoutineControl結果を表示する場合、選択済み項目に追加
        this.handleAppendButtonClick();
        this.updateFilter();

        // 表示項目がない場合、非サポート画面を表示
        if (this.originalFunctionList.length < 1 && this.selectedAtFunc.data_monitor) {
          // OK押下時は何もしない
          alert.showConfirmation(this, null, this.$t('individual.label_not_supported_message'), null, null, null, true);
        }
      } finally {
        this.processing = false;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => {
          self;
          this.logout(this);
        },
        // セッションリセット処理のコールバック
        (self) => {
          self;
          this.resetSession(this);
        },
        // その他エラー発生のコールバック
        (result) => {
          result;
          this.showUnexpectedError();
        }
      );
    },

    /**
     * データモニタの多言語変換の一覧取得
     * @param {Array} monitorResponse メニュー一覧
     */
    getDmi18nMonitorItemList(monitorResponse) {
      let findFFDi18nKey = '';
      let previousSystem = '';
      // 多言語変換リストの取得
      return monitorResponse
        .map((item) => {
          const systemName = dmUtil.getSystem(item.id);
          // 異なるシステムの信号線項目の場合は、翻訳用の接頭辞を作り直す
          if (previousSystem !== systemName) {
            findFFDi18nKey = individualDiagnose.getFFDi18nKey(this, (this.individualMenus ? this.individualMenus.menu_list : []), systemName);
            previousSystem = systemName;
          }
          let i18nDmItemKey = `${findFFDi18nKey}.${dmUtil.getNoSystemItemId(item.id)}`;
          // HEV系AT かつ 1行目表示IDの場合、翻訳キーを変更
          if (this.isHev) {
            const sameIndex = this.atInfo.findIndex(element => element.id === dmUtil.getNoSystemItemId(item.id));
            if (sameIndex !== -1) {
              // atInfo内typeの1文字目を大文字に変更してキーを作成
              i18nDmItemKey = `at${systemName}.${this.selectedAtFunc.active_test_id.slice(0, -2)}.menu_title`;
            }
          }
          return {
            data_monitor_id: item.id,
            sort_id: item.monitor_display_order,
            menu_title: util.trimStr(this.$t(i18nDmItemKey)),
            system_name: dmUtil.getAbbrSysName(systemName)
          };
        })
        .sort(this.itemComparator);
    },

    /**
     * データモニタの多言語変換の一覧取得
     * @param {Array} selectedList メニュー一覧
     */
    getDmi18nMenuSelectedList(selectedList) {
      let findFFDi18nKey = '';
      let previousSystem = '';
      // 多言語変換リストの取得
      return selectedList
        .map((item) => {
          const systemName = dmUtil.getSystem(item.data_monitor_id);
          // 異なるシステムの信号線項目の場合は、翻訳用の接頭辞を作り直す
          if (previousSystem !== systemName) {
            findFFDi18nKey = individualDiagnose.getFFDi18nKey(this, (this.individualMenus ? this.individualMenus.menu_list : []), systemName);
            previousSystem = systemName;
          }
          let i18nDmItemKey = `${findFFDi18nKey}.${dmUtil.getNoSystemItemId(item.data_monitor_id)}`;
          // HEV系AT かつ 1行目表示IDの場合、翻訳キーを変更
          if (this.isHev) {
            const sameIndex = this.atInfo.findIndex(element => element.id === dmUtil.getNoSystemItemId(item.data_monitor_id));
            if (sameIndex !== -1) {
              // active_test_idの末尾2桁を削除した上でキーを作成
              i18nDmItemKey = `at${systemName}.${this.selectedAtFunc.active_test_id.slice(0, -2)}.menu_title`;
            }
          }
          return {
            data_monitor_id: item.data_monitor_id,
            menu_title: util.trimStr(this.$t(i18nDmItemKey)),
            pullDownList: item.pullDownList,
            pullDownVisible: item.pullDownVisible,
            selectItem: item.selectItem,
            sort_id: item.sort_id,
            system_name: dmUtil.getAbbrSysName(systemName)
          };
        })
        .filter((item) => item.menu_title)
        .sort(this.itemComparator);
    },

    /**
     * 選択した機能を変数に保持
     */
    clickRow: function (selected) {
      // cstm-tableのソート順を取得
      const sortDir =
        this.$refs.filtered_tbl.currentSortDir === config.ASC
          ? config.DESC
          : config.ASC;
      const sort_now = this.$refs.filtered_tbl.currentSort;

      this.selectedFunction = this.originalFunctionList.find(
        (func) => func.data_monitor_id === selected.data_monitor_id
      );
      const idx = this.selectedFunctions.findIndex(
        (item) => item.data_monitor_id === this.selectedFunction.data_monitor_id
      );

      if (idx === -1) {
        this.selectedFunctions.push(this.selectedFunction);
      } else {
        this.selectedFunctions.splice(idx, 1);
      }

      this.setHighLight();
      this.selectedRemoveFunction = {};
      this.setHighLightRemove();

      // cstm-tableのソートを再実行
      this.currentSortkey = sort_now;
      this.$refs.filtered_tbl.currentSort = sort_now;
      this.$refs.filtered_tbl.currentSortDir = sortDir;
      this.$refs.filtered_tbl.sort({ name: sort_now });
      this.$refs.filtered_tbl.getSortIcon();
    },

    /**
     * 選択した機能を変数に保持
     */
    clickRowRemove: function (selected) {

      // HEV系AT: IOControl, RoutineControl対象の場合何もしない
      if (
        this.isHev &&
        this.atInfo.findIndex(item => item.id === dmUtil.getNoSystemItemId(selected.data_monitor_id)) !== -1
      ) return;

      this.selectedRemoveFunction = selected;
      const idx = this.selectedRemoveFunctions.findIndex(
        (item) =>
          item.data_monitor_id === this.selectedRemoveFunction.data_monitor_id
      );

      if (idx === -1) {
        this.selectedRemoveFunctions.push(selected);
      } else {
        this.selectedRemoveFunctions.splice(idx, 1);
      }

      this.setHighLight();
      this.selectedFunction = {};
      this.setHighLightRemove();
    },

    /**
     * 引数listにHEV系ATのDM１行目表示対象IDが含まれない場合、追加して返す
     * @param {*} list 
     */
    setSelectedFunctionList(list) {
      if (!this.isHev) return list;
      const returnList = list;
      this.atInfo.forEach(item => {
        if (returnList.findIndex(element => dmUtil.getNoSystemItemId(element.data_monitor_id) === item.id) === -1) {
          const targetItem = this.originalFunctionList.find(element =>
            dmUtil.getNoSystemItemId(element.data_monitor_id) === item.id
          );
          returnList.unshift({
            ...targetItem,
            pullDownVisible: true,
            selectItem: "1"
          });
        }
      });
      return returnList;
    },

    /**
     * HEV系AT対応：IO/Routine ControlのDM表示対象項目をエスケープ
     * @param {array} list 元リスト
     * @returns {array} 修正済みリスト
     */
    escapeHevTarget(list) {
      if (!this.isHev) return list;
      const returnList = JSON.parse(JSON.stringify(list));
      this.atInfo.forEach(item => {
        const indexToRemove = returnList.findIndex(element => dmUtil.getNoSystemItemId(element.data_monitor_id) === item.id);
        if (indexToRemove !== -1) {
          returnList.splice(indexToRemove, 1);
        }
      });
      return returnList;
    },

    /**
     * 選択している機能のハイライト設定
     */
    setHighLight() {
      this.filterFunctionList.forEach((item) => {
        let matchFlg = false;
        this.selectedFunctions.forEach((data) => {
          if (item.data_monitor_id === data.data_monitor_id) {
            item.fieldSpec = {
              menu_title: config.LIST_ITEM_HIGHLIGHT,
            };
            matchFlg = true;
          }
        });
        if (!matchFlg) {
          delete item.fieldSpec;
        }
      });
    },

    /**
     * 選択している機能のハイライト設定
     */
    setHighLightRemove() {
      this.filterRemoveFunctionList.forEach((item) => {
        let matchFlg = false;
        this.selectedRemoveFunctions.forEach((data) => {
          if (item.data_monitor_id === data.data_monitor_id) {
            item.fieldSpec = {
              menu_title: config.LIST_ITEM_HIGHLIGHT,
            };
            matchFlg = true;
          }
        });
        if (!matchFlg) {
          delete item.fieldSpec;
        }
      });
    },

    /**
     * システム名が選択されたイベント処理
     */
    changeSystem() {
      // 選択されたシステム名の項目のみ表示
      this.filterFunctionList = this.escapeHevTarget(this.filterBySystem(this.originalFunctionList));
      // シグナルグループはデフォルトに戻す
      this.selectedSignalGroup = null;
      this.filteredSignalGroupList = this.signalGroupList.filter(signal => (
        !signal.system || signal.system === this.selectedSystem
      ));
      this.updateFilter();
    },

    /**
     * 選択状態を解除し、フィルターの文字列、選択済みの項目に応じてリストをフィルターする
     */
    updateFilter() {
      this.currentSortkey = "";
      this.selectedFunctions = [];
      this.selectedFunction = {};
      const groupFilteredList = this.filterBySignalGroup(this.selectedSignalGroup);
      if (groupFilteredList && groupFilteredList.length > 0) {
        this.filterFunctionList = this.escapeHevTarget(groupFilteredList.filter(
          // 選択済みの項目をフィルターする
          (item) =>
            !this.selectedFunctionList.some(selectedItem => selectedItem.data_monitor_id === item.data_monitor_id)
        ).filter(
          // 翻訳後の名称を大文字・小文字区別なくフィルターする
          (item) =>
            item.menu_title
              .toLowerCase()
              .includes(this.filterText.toLowerCase())
        ));
      } else {
        this.filterFunctionList = [];
      }
      this.setHighLightRemove();
      this.setHighLight();
    },

    /**
     * 信号グループに応じてリストを絞り込む関数
     * @param {string} signalGroup 
     * @returns filteredList 信号グループでフィルターされた項目
     */
    filterBySignalGroup(signalGroup) {
      let filteredList = [];
      if (this.signalGroupList.some((group) => group.value === signalGroup)) {
        const filteredId = this.signalGroupItems.get(signalGroup);
        filteredList = this.filterBySystem(this.originalFunctionList).filter((list) => filteredId.includes(list.data_monitor_id));
      } else if (signalGroup === null) {
        //初期状態
        filteredList = this.filterBySystem(this.originalFunctionList);
      }
      return filteredList;
    },

    /**
     * 選択中のシステムで、項目をフィルタする
     * @param {array} originalFunctions フィルタする元のリスト
     * @returns {array} システムでフィルターされた項目
     */
    filterBySystem(originalFunctions) {
      return originalFunctions.filter(f => f.data_monitor_id.startsWith(this.selectedSystem));
    },

    /**
     * 選択状態を解除し、フィルターの文字列に応じてリストをフィルターする
     */
    updateFilterRemove() {
      this.selectedRemoveFunctions = [];
      this.selectedRemoveFunction = {};
      if (this.selectedFunctionList && this.selectedFunctionList.length > 0) {
        this.filterRemoveFunctionList = this.selectedFunctionList.filter(
          // 翻訳後の名称を大文字・小文字区別なくフィルターする
          (item) =>
            item.menu_title
              .toLowerCase()
              .includes(this.filterTextRemove.toLowerCase())
        );
      } else {
        this.filterRemoveFunctionList = [];
      }
      this.setHighLight();
      this.setHighLightRemove();
    },

    /**
     * カラムを設定
     */
    setColumns() {
      this.systemTableColumns = [
        {
          id: "menu_title",
          name: "menu_title",
          label: this.$t("individual.header_data_monitor_table_item"),
          useHtmlBind: true,
        },
      ];
      this.cstmTableColumns = [
        {
          id: "menu_title",
          name: "menu_title",
          label: this.$t("individual.header_data_monitor_table_item"),
          useHtmlBind: true,
          originalSortFunc: (param) => {
            // 選択候補のテーブルのcurrentSortkeyは、ソート時でなく項目選択時に設定される。
            // ソート後に項目を選択していないと再描画でソートが解除されるため、計測対象のテーブルのソート時でも設定する
            this.currentSortkey = this.$refs.filtered_tbl.currentSort;

            this.selectedTableSort.currentSortKey = param.sortKey;
            this.selectedTableSort.column[COLUMN.NAME] = param.sortDir;
            // 第2ソートキーはシステム
            const tempSorted = util.sortItems(param.data, COLUMN.SYSTEM, this.selectedTableSort.column[COLUMN.SYSTEM]);
            // 第1ソートキーは項目
            const resultItems = util.sortItems(tempSorted, param.sortKey, param.sortDir);
            this.selectedFunctionList = this.setSelectedFunctionList(resultItems);
            return resultItems;
          }
        },
      ];

      if (this.dataMonitorFunctionSettings.isSystemSelect) {
        // 計測対象のテーブルにシステム列を追加
        this.cstmTableColumns.unshift({
          id: "system_name",
          name: "system_name",
          label: this.$t('message.header_data_monitor_table_item_ecu'),
          useHtmlBind: true,
          originalSortFunc: (param) => {
            this.currentSortkey = this.$refs.filtered_tbl.currentSort;

            this.selectedTableSort.currentSortKey = param.sortKey;
            this.selectedTableSort.column[COLUMN.SYSTEM] = param.sortDir;
            // 第2ソートキーは項目
            const tempSorted = util.sortItems(param.data, COLUMN.NAME, this.selectedTableSort.column[COLUMN.NAME]);
            // 第1ソートキーはシステム
            const resultItems = util.sortItems(tempSorted, param.sortKey, param.sortDir);
            this.selectedFunctionList = this.setSelectedFunctionList(resultItems);
            return resultItems;
          }
        });
      }
    },

    /**
    *  項目追加
    */
    appendFunction(functionList) {

      // 項目リストの有無チェック(初期化通信時にエラーが発生した場合のフェールセーフ)
      if (!functionList) return;

      // 項目リスト-選択項目リスト差分
      const tmpListFull = functionList
        .filter((e) => {
          return !this.selectedFunctionList.some(
            (ele) => e.data_monitor_id === ele.data_monitor_id
          );
        });
      let sortTmpListFull = [];
      this.systemId.forEach(id => (
        sortTmpListFull = sortTmpListFull.concat(this.escapeHevTarget(tmpListFull
          .filter(f => f.data_monitor_id.startsWith(id))
        ).sort((a, b) => a.sort_id - b.sort_id))
      ));

      const tmplist = sortTmpListFull.slice(0, functionList.length).map((e) => {
        return {
          data_monitor_id: e.data_monitor_id,
          sort_id: e.sort_id,
          menu_title: e.menu_title,
          system_name: e.system_name,
          pullDownVisible: true, // 設定値プルダウンの有無
          selectItem: "1", // プルダウンの設定値
        };
      });

      this.selectedFunctionList = this.setSelectedFunctionList(
        [...new Set(this.selectedFunctionList.concat(tmplist))]
      );
      this.selectedFunctionListPre = JSON.parse(
        JSON.stringify(this.selectedFunctionList)
      );
      this.selectedFunction = {};
      this.selectedFunctions = [];
      this.selectedRemoveFunction = {};
      this.selectedRemoveFunctions = [];
      this.setHighLight();
      this.filterRemoveFunctionList = this.selectedFunctionList;
      this.updateFilterRemove();
      this.updateFilter();
    },

    /**
     *  追加ボタン押下時
     */
    handleAppendButtonClick() {
      this.appendFunction(this.selectedFunctions);
    },

    /**
     * 削除ボタン押下時
     */
    handleRemoveButtonClick() {
      this.selectedRemoveFunctions.forEach((data) => {
        const idx = this.selectedFunctionList.findIndex(
          (item) => item.data_monitor_id === data.data_monitor_id
        );
        if (idx > -1) {
          this.selectedFunctionListPre.splice(idx, 1);
          this.selectedFunctionList.splice(idx, 1);
        }
      });

      this.selectedRemoveFunction = {};
      this.selectedRemoveFunctions = [];
      this.selectedFunction = {};
      this.selectedFunctions = [];
      this.setHighLight();
      this.filterRemoveFunctionList = this.selectedFunctionList;
      this.updateFilterRemove();
      this.updateFilter();
    },

    /**
     * 全追加ボタン押下時
     */
    handleAllAppendButtonClick() {
      this.appendFunction(this.filterFunctionList);
    },

    /**
     * 全削除ボタン押下時
     */
    handleAllRemoveButtonClick() {
      this.selectedRemoveFunction = {};
      this.selectedRemoveFunctions = [];
      this.selectedFunction = {};
      this.selectedFunctions = [];
      this.selectedFunctionList = this.setSelectedFunctionList([]);
      this.selectedFunctionListPre = [];
      this.filterRemoveFunctionList = this.setSelectedFunctionList([]);
      this.selectedTableSort.currentSortKey = "";
      this.setHighLight();
      this.setHighLightRemove();
      this.updateFilter();
    },

    /** 計測開始 */
    startExecute() {
      if (this.selectedFunctionList && this.selectedFunctionList.length === 0 && this.isDataMonitor)
        return;
      // custom_listの更新
      this.signalGroupItems.set('custom_list', this.selectedFunctionList.map((list) => list.data_monitor_id));
      this.$emit("start-execute", this.selectedFunctionList);
    },

    /**
     * その他のエラー
     */
    showUnexpectedError() {
      alert.showError(
        this,
        null,
        this.$t("individual.error_Initialize_communication_error"),
        () => { }
      );
    },

    /**
     * 機能リストのエスケープ
     * @returns エスケープした機能リスト
     */
    escapeFunctionList(items) {
      return items.map(i => ({
        ...i,
        menu_title: util.escapeHTML(i.menu_title)
      })
      );
    },

    /**
     * TSVファイルダウンロード処理(DEBUG用)
     */
    downloadTSV() {
      const tsvItems = this.filterFunctionList.map((filterFunction, index) => ({
        data_monitor_id: filterFunction.data_monitor_id,
        name: filterFunction.menu_title,
        order: index
      }));
      dmUtil.tsvFileExport(tsvItems, 'data_monitor_select_list.tsv');
    },

    /**
     * デバッグ用のコンポーネントを表示する(DEBUG用)
     */
    showDebugButton() {
      if (this.isDebug) {
        this.showDebug = true;
      }
    },

    /**
     * 項目リストのインポート押下
     */
    showConfigImportModal() {
      // 初期化
      this.importFile = null;
      this.fileName = null;
      this.phase = PHASE.CONFIRM_IMPORT;
      this.showCstmListModal = true;
    },

    /**
     * 項目リストのエクスポート押下
     */
    showConfigExportModal() {
      this.phase = PHASE.CONFIRM_EXPORT;
      this.showCstmListModal = true;
    },

    /**
     * ファイル選択のダイアログを表示
     */
    openFolder() {
      // 初期化
      this.importFile = null;
      this.fileName = null;
      this.$refs.input.click();
    },

    /**
     * 選択されたファイル情報を保持
     * @param {} file 
     */
    selectFile(file) {
      const fileData = file.target.files[0];
      if (fileData) {
        this.importFile = fileData;
        this.fileName = fileData.name;
      }
    },

    /**
     * インポート実行
     */
    importItemList() {
      reader.onload = (event) => {
        this.processing = true;
        individualDiagnose.getCurrentSystemId(this);
        let _importedItemList;
        try {
          _importedItemList = JSON.parse(event.target.result);
          // 指定ファイルのシステムIDが選択しているECUと一致しているか確認
          const tmpArray = [];
          _importedItemList.forEach((item) => {
            if (this.systemId.includes(item.system)) {
              tmpArray.push(item);
            }
          });
          // システムIDが一致しているデータがない場合は、システムID不一致エラーに遷移
          if (tmpArray.length === 0) throw new Error(MESSAGE.ERROR_SYSTEM_ID);
          // 選択済みの項目をリセット
          this.handleAllRemoveButtonClick();
          // 指定ファイルからselectedFunctionsを生成
          this.createItemListFromImport(tmpArray);
          this.appendFunction(this.selectedFunctions);
          this.phase = PHASE.IMPORT_SUCCESS;
        } catch (error) {
          if (error.message === MESSAGE.ERROR_SYSTEM_ID) {
            // システムID不一致エラー
            this.phase = PHASE.ERROR_SYSTEM_ID;
          } else {
            // フォーマットエラー
            this.phase = PHASE.ERROR_FORMAT;
          }
        } finally {
          this.processing = false;
        }
      };
      reader.readAsText(this.importFile);
    },

    /**
     * エクスポート実行
     */
    exportItemList() {
      // 選択済みリストの情報json化
      const tmpData = this.getExportData();
      // ファイル生成用のオブジェクト作成
      const link = window.document.createElement('a');
      const blob = new Blob([tmpData], { type: 'application/json;charset=utf-8' });
      try {
        // 日時情報の取得
        const date = moment().format("YYYYMMDDHHmmss");
        // 生成したファイルのダウンロード用処理
        link.download = 'itemList_' + date + '.json';
        link.href = window.URL.createObjectURL(blob);
        link.style.display = 'none';
        link.click();
      } catch (e) {
        // エラーログ出力
        this.logWrapper.log('item list export error.');
      } finally {
        // 生成したオブジェクトの解放
        if (link) link.remove();
        // モーダル非表示
        this.showCstmListModal = false;
      }
    },

    /**
     * エクスポート時のjson生成
     * @returns {JSON}
     */
    getExportData() {
      // システムIDとDIDの取得
      // 出力対象のリストから取得※data_monitor_idのタブ区切り1要素目がシステムID,2要素目がDID
      const tmpArray = [];
      this.selectedFunctionList.forEach((item) => {
        const arr = item.data_monitor_id.split("\t", 2);
        const obj = {
          "system": arr[0],
          "id": arr[1]
        };
        tmpArray.push(obj);
      });

      return JSON.stringify(tmpArray);
    },

    /**
     * インポートファイルからthis.selectedFunctionsを生成
     * @param {Object} importedList 
     */
    createItemListFromImport(importedList) {
      // 初期化
      this.selectedFunctions = [];
      importedList.forEach((item) => {
        // data_monitor_id生成
        const keyVal = item.system + "\t" + item.id;
        // data_monitor_idをキーにして他の情報を取得
        const obj = this.originalFunctionList.find(l => l.data_monitor_id === keyVal);
        if (obj) this.selectedFunctions.push(obj);
      });
    },

    /**
     * 文言の設定
     */
    setPlaceHolderMessage() {
      return this.$t('individual.label_data_monitor_item_list_import_form_placeholder');
    },

    /**
     * 共通の[キャンセル]ボタンクリック処理
     */
    onCancelClicked() {
      // 確認画面でしか使用されないため分岐なし
      this.showCstmListModal = false;
    },

    /**
     * 共通の[OK]ボタンクリック処理
     */
    onOkClicked() {
      switch (this.phase) {
        case PHASE.CONFIRM_IMPORT:
          // インポート実行
          this.importItemList();
          break;
        case PHASE.CONFIRM_EXPORT:
          // エクスポート実行
          this.exportItemList();
          break;
        case PHASE.IMPORT_SUCCESS:
        case PHASE.ERROR_FORMAT:
        case PHASE.ERROR_SYSTEM_ID:
          this.showCstmListModal = false;
          break;
        default:
          // noop
          break;
      }
    },


  },

  created() {
    // noop
  },

  mounted() {
    this.mountedLogic();
  },

  // Clear Timer
  clearWatcher: function () {
    if (this.watcher) {
      this.$stopInterval(this.watcher);
      this.watcher = null;
    }
    this.executeRspMsgCheckTimeout = Number(
      process.env.VUE_APP_MAC_CC_RSP_MAX_INTERVAL
    );
  },
};
